import { FreightLaneCostSortOption } from '@rematter/frontend-lib-api-client';

import { FreightLaneCostRecord } from '~/modules/transportation/types';
import { createQueryHook } from '~/shared/providers/api';
import { parseZonedDateTime } from '~/shared/utils/dateTime';

export interface FreightLaneCostsFilter {
  ids?: string[];
  freightLaneId?: string;
}

interface GetFreightLaneCostsVariables {
  filter?: FreightLaneCostsFilter;
  sorting?: FreightLaneCostSortOption[];
  pagination?: {
    offset: number;
    limit: number;
  };
}

export const useFreightLaneCostsListQuery = createQueryHook(
  'transportation.freightLaneCostsList',
  (variables: GetFreightLaneCostsVariables) => ({
    freightLaneCosts: {
      __args: {
        filter: variables.filter,
        pagination: variables.pagination,
        sorting: variables.sorting ?? ['StartDateDesc'],
      },
      page: {
        total: true,
      },
      entries: {
        id: true,
        freightLaneId: true,
        startDate: true,
        endDate: true,
        rateFormula: true,
        rateVariableIds: true,
        minimumChargeAmount: true,
        minimumChargeCurrencyCode: true,
        chargebackFormula: true,
        chargebackVariableIds: true,
        minimumChargebackAmount: true,
        minimumChargebackCurrencyCode: true,
        fuelSurchargeFormula: true,
        fuelSurchargeVariableIds: true,
        fuelSurchargeMode: true,
        currency: true,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
    },
  }),
  {
    select: (
      data,
    ): { freightLaneCosts: FreightLaneCostRecord[]; total: number } => ({
      freightLaneCosts: (data?.freightLaneCosts?.entries ?? []).map(
        (entry) => ({
          id: entry.id,
          freightLaneId: entry.freightLaneId,
          startDate: entry.startDate,
          endDate: entry.endDate ?? undefined,
          rateFormula: entry.rateFormula ?? undefined,
          rateVariableIds: entry.rateVariableIds ?? undefined,
          minimumChargeAmount: entry.minimumChargeAmount ?? undefined,
          minimumChargeCurrencyCode:
            entry.minimumChargeCurrencyCode ?? undefined,
          chargebackFormula: entry.chargebackFormula ?? undefined,
          chargebackVariableIds: entry.chargebackVariableIds ?? undefined,
          minimumChargebackAmount: entry.minimumChargebackAmount ?? undefined,
          minimumChargebackCurrencyCode:
            entry.minimumChargebackCurrencyCode ?? undefined,
          fuelSurchargeFormula: entry.fuelSurchargeFormula ?? undefined,
          fuelSurchargeVariableIds: entry.fuelSurchargeVariableIds ?? undefined,
          fuelSurchargeMode: entry.fuelSurchargeMode ?? undefined,
          currency: entry.currency,
          timestamps: {
            createdAt: parseZonedDateTime(entry.timestamps.createdAt),
            updatedAt: parseZonedDateTime(entry.timestamps.updatedAt),
          },
        }),
      ),
      total: data?.freightLaneCosts?.page?.total ?? 0,
    }),
  },
);
